import React, { useEffect, useCallback, useState } from 'react'
import { useEsri } from '../hooks'

import { BASE_URL, ICONS } from '../../constants'

import GraphicLayer from './GraphicLayer'

const esriReqs = ['esri/core/watchUtils']

const clusterTolerance = 0.0004

const clusterTest = (resolution) => (a) => (b) => {
  const distance = Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2))
  return distance <= clusterTolerance * resolution
}

const makeClusters = (graphics, resolution) => {
  let clusters = []
  const clusterTestForResolution = clusterTest(resolution)
  graphics.forEach((graphic) => {
    const isTooClose = clusterTestForResolution(graphic.geometry)
    for (let i = 0; i < clusters.length; i++) {
      const cluster = clusters[i]
      if (isTooClose(cluster[0].geometry)) {
        cluster.push(graphic)
        return
      }
    }
    clusters.push([graphic])
  })
  return clusters
}

const makeClusterSymbol = (count, isSelected) => {
  const size = count > 99 ? '75px' : '50px'
  const image = isSelected ? 'circleClusterActive.png' : 'circleCluster.png'
  return {
    type: 'picture-marker',
    url: `${BASE_URL}${ICONS.DIR}${image}`,
    height: size,
    width: size,
  }
}

const makeClusterGraphic = (cluster, getIsSelected) => ({
  geometry: cluster[0].geometry,
  symbol: makeClusterSymbol(cluster.length, cluster.some(getIsSelected)),
  attributes: {
    type: 'cluster',
    cluster,
  },
})

const makeLabelSymbol = (text) => ({
  type: 'text',
  color: 'white',
  haloColor: 'black',
  haloSize: '1px',
  text,
  xoffset: 0,
  yoffset: -3,
  font: {
    size: 16,
    weight: 'bold',
  },
})

const makeLabelGraphic = (cluster) => ({
  geometry: cluster[0].geometry,
  symbol: makeLabelSymbol(cluster.length),
  attributes: {
    cluster,
    type: 'label',
  },
})

const makeSingleGraphic = (cluster) => ({
  geometry: cluster[0].geometry,
  symbol: cluster[0].symbol,
  attributes: {
    cluster,
    type: 'single',
  },
})

const generateClusterGraphics = (clusters, getIsSelected) =>
  clusters.flatMap((cluster) => {
    if (cluster.length > 1) {
      const labelGraphic = makeLabelGraphic(cluster)
      const clusterGraphic = makeClusterGraphic(cluster, getIsSelected)
      // TODO add individual cluster points?
      return [clusterGraphic, labelGraphic]
    } else {
      return makeSingleGraphic(cluster)
    }
  })

const ClusterLayer = ({
  id,
  map,
  view,
  graphics,
  getIsSelected,
  onSelect,
  onDeselect,
}) => {
  const [ready, { watchUtils }] = useEsri(esriReqs)
  const [clusterResolution, setClusterResolution] = useState(1)

  const [clusterGraphics, setClusterGraphics] = useState([])

  // Refresh graphics when incidents change, or selected incident changes
  useEffect(() => {
    if (graphics) {
      const clusters = makeClusters(graphics, clusterResolution)
      const newClusterGraphics = generateClusterGraphics(
        clusters,
        getIsSelected
      )
      setClusterGraphics(newClusterGraphics)
    }
  }, [graphics, getIsSelected, clusterResolution])

  const onSelectCluster = useCallback(
    (graphics) => {
      if (graphics.length > 1) {
        console.error('clusters overlapping')
      }
      onSelect(graphics[0].attributes.cluster)
    },
    [onSelect]
  )

  // Track cluster resolution on extent change
  useEffect(() => {
    if (ready && view && watchUtils) {
      watchUtils.whenTrue(view, 'stationary', () => {
        const resolution =
          ((view.extent && view.extent.width) || 0) / view.width
        if (resolution !== clusterResolution) setClusterResolution(resolution)
      })
    }
  }, [view, ready, watchUtils, clusterResolution, setClusterResolution])

  return (
    <GraphicLayer
      id={id}
      map={map}
      view={view}
      graphics={clusterGraphics}
      onSelect={onSelectCluster}
      onDeselect={onDeselect}
    />
  )
}

export default ClusterLayer
